import React, { useState, useEffect } from "react";
import {
  fetchConcernsThunk,
  fetchConversationThunk,
  postConcernConversationThunk,
} from "../../redux/Slices/concernsListSlice";
import { useDispatch, useSelector } from "react-redux";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import TicketForm from "./TicketForm";
import Loader from "../../components/Common/Loader";
import { CATEGORY_TICKET } from "../../utils/Constant/Constant";
import { formatDate, formatTime } from "../../utils/Utils";

const MessageList = ({ setIsMessageListOpen }) => {
  const dispatch = useDispatch();
  const { profileData } = useSelector((state) => state.profile);
  const { trans } = useCustomTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [sampleTickets, setSampleTickets] = useState([]);
  const [conversation, setConversation] = useState([]);
  const coachId = profileData?.coachId;
  const { concernsList, isloading, conversationList, isLoadingConversation } =
    useSelector((state) => state.getConcerns);

  useEffect(() => {
    setSampleTickets(concernsList);
  }, [concernsList]);

  useEffect(() => {
    dispatch(fetchConcernsThunk(coachId));
  }, [dispatch, coachId]);

  useEffect(() => {
    setConversation(conversationList || []);
  }, [conversationList]);

  const handleContactSupportClick = (e) => {
    e.preventDefault();
    setIsFormOpen(true);
  };

  const handleMessageClick = (message) => {
    dispatch(fetchConversationThunk(message.issueId));
    setSelectedMessage(message);
  };

  const handleSendResponse = async (issueId) => {
    if (responseText.trim()) {
      const message = {
        coachId,
        issueId,
        issueMessage: responseText,
      };
      const response = await dispatch(
        postConcernConversationThunk({ message })
      );

      if (response.meta.requestStatus === trans("concerns.fulfilled")) {
        setConversation((prev) => [
          ...prev,
          {
            sender: trans("concerns.coach"),
            issueMessage: response.payload.issueMessage,
          },
        ]);
      }
      setResponseText("");
      dispatch(fetchConversationThunk(issueId));
    }
  };

  return (
    <div className=" ticket-form">
      <div className="d-flex Message-heading-concers align-items-center">
        <h4 className="text-white text-center pt-2 flex-grow-1">
          {trans("concerns.message")}
        </h4>
        <button
          className="btn btn-close btn-lg text-white ms-auto mr-2 b-0 bg-transparent"
          onClick={() => setIsMessageListOpen(false)}
          style={{ background: "transparent", border: "none" }}
        >
          <i className="fs-3 bi bi-x mb-0 lh-1"></i>
        </button>
      </div>
      <div className="header bg-blue-900 text-white text-center py-3">
        <h5>{trans("concerns.message")}</h5>
      </div>

      <div className="message-list">
        {isloading ? (
          <div className="d-flex justify-content-center align-items-center py-4">
            <Loader />
          </div>
        ) : (
          <>
            {!isFormOpen &&
              !selectedMessage &&
              (sampleTickets?.length > 0 ? (
                sampleTickets.map((message) => (
                  <div
                    key={message.issueId}
                    className="message-item"
                    onClick={() => handleMessageClick(message)}
                  >
                    <div className="message-row">
                      <p className="fs-5 fw-bold text-truncate p-0 m-0">
                        {message.issueSubject}
                      </p>
                      <div className="message-arrow">
                        <i className="bi bi-chevron-right"></i>
                      </div>
                    </div>
                    <div className="message-details">
                      <span>
                        {CATEGORY_TICKET.find(
                          (item) => item.value === message.category
                        )?.label || trans("concerns.unknownCategory")}
                      </span>
                      <span
                        className={`status-dot ${
                          message.issueStatus === 0
                            ? "status-active"
                            : message.issueStatus === 1
                            ? "status-resolved"
                            : "status-inactive"
                        }`}
                      ></span>
                    </div>
                    <p className="p-0 m-0">
                      <span>{formatDate(message.createdOn)}</span>
                    </p>
                  </div>
                ))
              ) : (
                <p className="no-tickets-message text-center py-4 p-0 m-0">
                  {trans("concerns.noTickets")}
                </p>
              ))}

            {!selectedMessage && (
              <div className="contact-support-container">
                <button
                  className="contact-support-button"
                  onClick={handleContactSupportClick}
                >
                  {trans("concerns.contactSupport")}{" "}
                  <i className="bi bi-send"></i>
                </button>
              </div>
            )}

            {isFormOpen && (
              <TicketForm coachId={coachId} setIsFormOpen={setIsFormOpen} />
            )}

            {selectedMessage && (
              <div className="message-details-screen p-3">
                <p>
                  <strong>{trans("concerns.category")}: </strong>
                  {CATEGORY_TICKET.find(
                    (item) => item.value === selectedMessage.category
                  )?.label || trans("concerns.unknownCategory")}
                </p>
                <p>
                  <strong>{trans("concerns.subject")}: </strong>
                  {selectedMessage.issueSubject}
                </p>
                <p>
                  <strong>{trans("concerns.description")}: </strong>
                  {selectedMessage.description}
                </p>

                <div className="messages message-scroll mt-3">
                  {isLoadingConversation ? (
                    <Loader message="" />
                  ) : (
                    <>
                      {conversation && conversation.length > 0 ? (
                        <>
                          {(() => {
                            // Group messages by date (yyyy-mm-dd)
                            const groupedMessages = conversation.reduce(
                              (groups, msg) => {
                                const date = new Date(
                                  msg.createdOn
                                ).toLocaleDateString();
                                if (!groups[date]) groups[date] = [];
                                groups[date].push(msg);
                                return groups;
                              },
                              {}
                            );
                            // Get ordered dates
                            const orderedDates = Object.keys(
                              groupedMessages
                            ).sort((a, b) => new Date(a) - new Date(b));

                            return orderedDates.map((date) => (
                              <div key={date}>
                                <div className="chat-date text-center text-muted my-2">
                                  {date}
                                </div>
                                {groupedMessages[date].map((msg, idx) => (
                                  <div
                                    key={idx}
                                    className={`chat-bubble-wrapper ${
                                      msg.sender === "Coach"
                                        ? "chat-right"
                                        : "chat-left"
                                    }`}
                                  >
                                    <div
                                      className={`chat-bubble ${
                                        msg.sender === "Coach"
                                          ? "bg-coach"
                                          : "bg-user"
                                      }`}
                                    >
                                      <p className="chat-message p-0 m-0">
                                        {msg.issueMessage}
                                        <p className="chat-time text-white mt-2 ">
                                          {formatTime(msg.createdOn)}
                                        </p>
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ));
                          })()}
                        </>
                      ) : (
                        <div className="text-muted">
                          {trans("concerns.noMessages")}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <textarea
                  className="w-full mt-3 p-2 border rounded resize-none"
                  style={{ height: "60px" }}
                  placeholder={trans("concerns.typeResponse")}
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                />
                <div className="d-flex send-back gap-2">
                  <button
                    className="contact-support-button w-full"
                    onClick={() => handleSendResponse(selectedMessage.issueId)}
                  >
                    {trans("concerns.send")}
                  </button>
                  <button
                    className="contact-support-button w-full bg-gray-600 hover:bg-gray-700"
                    onClick={() => setSelectedMessage(null)}
                  >
                    {trans("concerns.backToMessages")}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MessageList;
